<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { formatGib } from "../format";
import type { Overview } from "../types/api";
import DailyBars, { type DayBar } from "./DailyBars.vue";

const props = defineProps<{ data: Overview; now: number }>();

const order = ref<"date" | "usage">("date");
const months = computed(() =>
  [...new Set((props.data.daily_traffic ?? []).map((row) => row.date.slice(0, 7)))].sort().reverse()
);
const month = ref("");
watch(
  months,
  (value) => {
    if (!value.includes(month.value)) month.value = value[0] ?? "";
  },
  { immediate: true }
);

const monthly = computed(() => (props.data.daily_traffic ?? []).filter((row) => row.date.startsWith(month.value)));
const summary = computed(() =>
  monthly.value.reduce(
    (sum, row) => ({ rx: sum.rx + row.rx_bytes, tx: sum.tx + row.tx_bytes, total: sum.total + row.total_bytes }),
    { rx: 0, tx: 0, total: 0 }
  )
);
const rows = computed(() =>
  [...monthly.value].sort((a, b) =>
    order.value === "usage" ? b.total_bytes - a.total_bytes : b.date.localeCompare(a.date)
  )
);

const today = computed(() => new Date(props.now).toISOString().slice(0, 10));
const isCurrentMonth = computed(() => month.value === today.value.slice(0, 7));

/** Every day of the selected month up to today, so missing days show as gaps in the chart. */
const bars = computed<DayBar[]>(() => {
  if (!month.value) return [];
  const [year, monthIndex] = month.value.split("-").map(Number);
  const length = isCurrentMonth.value ? Number(today.value.slice(8, 10)) : new Date(Date.UTC(year, monthIndex, 0)).getUTCDate();
  const byDate = new Map(monthly.value.map((row) => [row.date, row]));
  return Array.from({ length }, (_, index) => {
    const date = `${month.value}-${String(index + 1).padStart(2, "0")}`;
    const row = byDate.get(date);
    return { date, total: row?.total_bytes ?? null, rx: row?.rx_bytes ?? null, tx: row?.tx_bytes ?? null };
  });
});

const updated = computed(() => Math.max(0, ...rows.value.map((row) => row.updated_at)));
const stale = computed(() => props.now / 1000 - updated.value > 900);
const monthLabel = (value: string) => `${value.slice(0, 4)} 年 ${Number(value.slice(5, 7))} 月`;
const updatedText = computed(() => new Date(updated.value * 1000).toISOString().replace("T", " ").slice(0, 16));
</script>

<template>
  <section class="traffic-section" aria-labelledby="traffic-heading">
    <div class="traffic-head">
      <div>
        <h2 id="traffic-heading">每月用量</h2>
        <p>{{ data.traffic_interface || "未配置网卡" }} · UTC 自然日 · 收发合计，单位 GiB</p>
      </div>
      <div class="traffic-controls">
        <label>
          月份
          <select v-model="month">
            <option v-for="item in months" :key="item" :value="item">{{ monthLabel(item) }}</option>
          </select>
        </label>
        <label>
          排序
          <select v-model="order">
            <option value="date">日期从新到旧</option>
            <option value="usage">用量从高到低</option>
          </select>
        </label>
      </div>
    </div>

    <p v-if="!rows.length" class="traffic-note">暂无每日记录，等待 vnStat 采集。</p>
    <template v-else>
      <dl class="month-summary">
        <div>
          <dt>月内合计</dt>
          <dd>{{ formatGib(summary.total) }}<span class="unit">GiB</span></dd>
        </div>
        <div>
          <dt>接收</dt>
          <dd>{{ formatGib(summary.rx) }}<span class="unit">GiB</span></dd>
        </div>
        <div>
          <dt>发送</dt>
          <dd>{{ formatGib(summary.tx) }}<span class="unit">GiB</span></dd>
        </div>
      </dl>

      <DailyBars :days="bars" :today="today" :height="128" />
      <p class="traffic-note month-note">
        {{ monthLabel(month) }} · 已记录 {{ rows.length }} 天 · {{ isCurrentMonth ? "本月持续累计中" : "按已有记录汇总" }}
      </p>

      <div class="traffic-table">
        <table>
          <thead>
            <tr>
              <th scope="col">日期（UTC）</th>
              <th scope="col">接收</th>
              <th scope="col">发送</th>
              <th scope="col">合计</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in rows" :key="row.date">
              <th scope="row">{{ row.date }}{{ row.date === today ? " · 今日" : "" }}</th>
              <td>{{ formatGib(row.rx_bytes) }}</td>
              <td>{{ formatGib(row.tx_bytes) }}</td>
              <td><strong>{{ formatGib(row.total_bytes) }}</strong></td>
            </tr>
          </tbody>
        </table>
      </div>
      <p class="traffic-note">
        更新于 {{ updatedText }} UTC{{ stale ? "（数据延迟）" : "" }}。今日为实时累计；月汇总按自然月计算，账单以 KiwiVM 为准。
      </p>
    </template>
  </section>
</template>

<style scoped>
.traffic-section { min-width: 0; }
.month-summary { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 1rem; margin-bottom: 2.75rem; }
.month-summary dt { color: var(--ink-3); font-size: 0.8125rem; }
.month-summary dd { margin-top: 0.15rem; font-size: clamp(1.25rem, 3.4vw, 1.875rem); font-weight: 500; letter-spacing: -0.02em; white-space: nowrap; }
.month-summary div:first-child dd { font-weight: 600; }
.month-note { margin: 0.25rem 0 1.75rem; }

@media (max-width: 560px) {
  .month-summary { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .month-summary div:first-child { grid-column: 1 / -1; }
}
</style>
